/**
 * Making a pot, and dividing it.
 *
 * Two screens over one draft (`pot.ts`), hosted by the page at `/prepare/new`.
 * They began as panes of the add sheet, on the theory that the moment a person
 * reaches for meal prep is the moment they are already looking at a meal. Two
 * things argued the other way and won. Nothing here is logged — the sheet's
 * one rule is instant writing, and this flow always broke it, reaching the
 * server once at "Store" with everything decided — so the sheet's reason for
 * existing (watch the ring move) did not apply. And a draft that lived in the
 * sheet died with it: Escape, a tap outside, the sheet closing for any reason,
 * emptied a pan somebody had spent five minutes filling. A pot is a statement
 * about a pan, not about a day, and it deserved an address.
 *
 * What the sheet established still holds here:
 *
 * - **Grams are typed on the sheet's own pad.** The system keyboard is for
 *   words and multi-field forms; every weight in this flow, ingredient or pot,
 *   opens `GramsPad`.
 * - **Colour stays rationed.** The macro marks and the primary action, and the
 *   one new hue is the amber on a pot that has to be eaten today — a signal,
 *   not decoration.
 *
 * The class names are still the sheet's (`sheet__scroll`, `sheet__heading`)
 * because the geometry is: a bounded column with one scroller and a docked
 * action. `.prepare-flow` in `app.css` gives the page that column.
 *
 * ## Why the builder is live
 *
 * Remove an ingredient here and the raw weight, the cooked weight, every
 * per-portion figure on the divider, and the ingredient list on the page a link
 * opens all move with it. That is not a nicety: the alternative is a divider
 * that quietly describes a pot the user has already changed, and a link sent to
 * a flatmate that lists an onion nobody put in.
 */

import {
  formatGrams,
  parseDecimal,
  perHundred,
  suggestedPortions,
  useFoodPortions,
  useFoodSearch,
  useFoods,
  useSaveRecipe,
  type FoodItem,
  type Recipe,
} from "@calorio/core";
import { useMemo, useState } from "react";

import { GramsPad } from "./GramsPad";
import { ScanPane, type Trust } from "./ScanPane";
import { useCalorio } from "../app/providers";
import type { PotActions, PotDraft, PotFigures, PotItem } from "./pot";
import { saveOffer } from "./pot";

const round = (value: number) => Math.round(value).toLocaleString();

/** ×1, ×2, ×3. Beyond three is a catering job, not a batch. */
const SCALES = [1, 2, 3];

/* ========================================================================== *
 * 7f — making the pot
 * ========================================================================== */

/** What the builder is doing right now. The pot itself is in the draft. */
type Step =
  | { kind: "list" }
  | { kind: "search" }
  | { kind: "scan" }
  /** Weighing something on its way in, or re-weighing a line already in. */
  | { kind: "weigh"; food: FoodItem; index: number | null }
  | { kind: "name" };

export function PotBuilder({
  draft,
  figures,
  actions,
  recipes,
  onDivide,
}: {
  draft: PotDraft;
  figures: PotFigures;
  actions: PotActions;
  recipes: Recipe[] | undefined;
  onDivide: () => void;
}) {
  const ctx = useCalorio();
  const [step, setStep] = useState<Step>({ kind: "list" });
  /**
   * The camera's two states, owned here rather than by the sheet.
   *
   * The sheet header says "take another photo" while scanning is a *mode* of
   * the sheet, and in this flow it is not: the header above says "New pot" and
   * its only button leaves the pot. So the pane inside the pot has to carry
   * both — the way back to the pot, and the way to thaw a viewfinder frozen on
   * a photograph — or a label that would not read is a dead end with the pot
   * behind it.
   */
  const [scanReset, setScanReset] = useState(0);
  const [scanFrozen, setScanFrozen] = useState(false);
  const [query, setQuery] = useState("");
  const [grams, setGrams] = useState("");
  const [name, setName] = useState(draft.name);
  const results = useFoodSearch(ctx, query);
  const saveRecipe = useSaveRecipe(ctx);

  const padFood = step.kind === "weigh" ? step.food : null;
  const habits = useFoodPortions(ctx, padFood?.id ?? null);

  /**
   * What this person has cooked with before.
   *
   * Off their own recipes rather than off Recent, and the difference matters:
   * Recent is what they have *eaten*, which is mostly finished products, and a
   * chip row of yoghurts and sandwiches is no use to somebody standing over a
   * pan. Ingredients are what recipes are made of, so that is where they are.
   */
  const pantryIds = useMemo(() => {
    const seen = new Map<string, number>();
    for (const recipe of recipes ?? []) {
      for (const item of recipe.items) {
        if (!seen.has(item.food_item_id)) seen.set(item.food_item_id, item.grams);
      }
    }
    for (const item of draft.items) seen.delete(item.food.id);
    return [...seen.entries()].slice(0, 12);
  }, [recipes, draft.items]);
  const pantry = useFoods(
    ctx,
    pantryIds.map(([id]) => id),
  );

  /* The ids of a chosen recipe's ingredients are known before the foods are, so
     loading a saved meal is two steps: pick, then fill once the foods arrive.
     Held here rather than in the draft because a half-loaded pot is not a pot. */
  const [loading, setLoading] = useState<Recipe | null>(null);
  const loadingIds = loading?.items.map((item) => item.food_item_id) ?? [];
  const loadingFoods = useFoods(ctx, loadingIds);
  if (loading && loadingIds.every((id) => loadingFoods.byId[id])) {
    const items: PotItem[] = loading.items.flatMap((item) => {
      const food = loadingFoods.byId[item.food_item_id];
      return food ? [{ food, grams: item.grams }] : [];
    });
    actions.fromRecipe(loading, items);
    setName(loading.name);
    setLoading(null);
  }

  const offer = saveOffer(draft, figures);
  const emptied = figures.empty && draft.source !== null;

  const weigh = (food: FoodItem, index: number | null, at?: number) => {
    setStep({ kind: "weigh", food, index });
    setGrams(at !== undefined ? String(Math.round(at)) : "");
    (document.activeElement as HTMLElement | null)?.blur();
  };

  const commitWeight = () => {
    if (step.kind !== "weigh") return;
    const quantity = parseDecimal(grams);
    if (!Number.isFinite(quantity) || quantity <= 0) return;
    if (step.index === null) actions.add(step.food, quantity);
    else actions.reweigh(step.index, quantity);
    setStep({ kind: "list" });
    setGrams("");
    setQuery("");
  };

  /** Back to the pot, from a viewfinder or from a photograph that would not read. */
  const backToPot = () => {
    setScanFrozen(false);
    setStep({ kind: "list" });
  };

  const scanned = (food: FoodItem, _trust: Trust) => {
    setScanFrozen(false);
    weigh(food, null);
  };

  const save = () => {
    saveRecipe
      .mutateAsync({
        input: {
          name: name.trim() || "New pot",
          servings_made: draft.portions,
          items: figures.lines.map((line) => ({
            food_item_id: line.food.id,
            grams: line.grams,
          })),
        },
      })
      .then((recipe) => {
        // The pot is now that recipe, cooked as written — which is what stops
        // the offer being made a second time, and what makes "Divide" send a
        // recipe id rather than a fresh composition.
        actions.fromRecipe(recipe, figures.lines.map((line) => ({
          food: line.food,
          grams: line.grams,
        })));
        actions.rename(recipe.name);
        setStep({ kind: "list" });
      })
      .catch(() => undefined);
  };

  if (step.kind === "scan") {
    return (
      <ScanPane
        resetToken={scanReset}
        onFrozen={setScanFrozen}
        onFound={scanned}
        tray={
          /* Both ways out, on every screen the pane can show — which is why the
             pane renders this tray beside a refusal as well as beside the
             viewfinder. A label that would not read used to leave the pot with
             no way back to it: the form for the food that failed filled the
             pane, and the only control still on screen was the sheet header's
             "Back to list", which throws the pot away. */
          <div className="pot__dock">
            {scanFrozen && (
              <button className="pot__back" onClick={() => setScanReset((n) => n + 1)}>
                Take another photo
              </button>
            )}
            <button className="pot__back" onClick={backToPot}>
              Back to the pot
            </button>
          </div>
        }
      />
    );
  }

  return (
    <>
      <div className="sheet__scroll pot">
        {!draft.started ? (
          <StartFrom
            recipes={recipes}
            query={query}
            onQuery={setQuery}
            onPick={setLoading}
            onScratch={() => {
              actions.fromScratch();
              // Unnamed rather than "New pot": the header already says that, and
              // an empty field is what invites a name.
              setName("");
              // Landing on the pot rather than straight in the search pane, so
              // the two ways in and the chip row of what this person cooks with
              // are on screen before anything covers them.
              setStep({ kind: "list" });
            }}
          />
        ) : (
          <>
            <div className="pot__head">
              <div className="pot__title">
                {/* The sheet header above already says "New pot", so printing
                    the draft's default name here said it twice and offered
                    nothing. A field says the same thing once and takes an
                    answer: a pot can be named the moment it goes on. */}
                <input
                  className="pot__rename"
                  value={name}
                  maxLength={80}
                  placeholder="Name this pot"
                  aria-label="Name this pot"
                  onChange={(event) => {
                    const next = event.target.value;
                    setName(next);
                    // The draft keeps a name at all times — it is what gets
                    // stored — so an emptied field falls back rather than
                    // storing a pot called nothing.
                    actions.rename(next.trim() === "" ? "New pot" : next.trim());
                  }}
                />
                <span className="pot__source">
                  {draft.source
                    ? `From your ${draft.source.name}${draft.scale === 1 ? "" : ` · ×${draft.scale}`}`
                    : "Built here, not saved yet"}
                </span>
              </div>

              {/* Leaving the pot no longer empties it — going to the list to
                  check something is a step within cooking, not the end of it —
                  so abandoning one has to be sayable somewhere. Here, small,
                  and away from every button that adds: this is the only control
                  in the flow that throws work away. */}
              <button
                className="pot__change"
                onClick={() => {
                  actions.reset();
                  setName("");
                  setStep({ kind: "list" });
                }}
              >
                Start over
              </button>
            </div>

            {draft.source && (
              <div className="pot__scale">
                <span className="pot__scale-label">Batch</span>
                {SCALES.map((value) => (
                  <button
                    key={value}
                    className={`pot__chip${value === draft.scale ? " is-on" : ""}`}
                    onClick={() => actions.setScale(value)}
                  >
                    ×{value}
                  </button>
                ))}
                {/* Said once, here, because "×2" beside a list of weights that
                    have already doubled otherwise reads as an instruction that
                    has not been carried out yet. */}
                <span className="pot__scale-note">the recipe, multiplied</span>
              </div>
            )}

            <div className="pot__section">
              <h3 className="sheet__heading">What went in</h3>
              <span className="pot__count num">
                {figures.lines.length}{" "}
                {figures.lines.length === 1 ? "ingredient" : "ingredients"} ·{" "}
                {round(figures.rawG)} g raw
              </span>
            </div>

            {emptied ? (
              <div className="pot__empty">
                <p className="pot__empty-title">You took everything out</p>
                <p className="muted">An empty pot has nothing to divide.</p>
                <button
                  className="pot__reload"
                  onClick={() => draft.source && setLoading(draft.source)}
                >
                  Reload the recipe
                </button>
              </div>
            ) : figures.empty ? (
              <div className="pot__empty">
                <p className="pot__empty-title">Nothing in the pot yet</p>
                <p className="muted">
                  Add what went in, at the weights you used.
                  {/* Only said when there is a row to point at. A first pot has
                      nothing cooked before it, and a sentence about chips that
                      are not there is the screen describing somebody else. */}
                  {pantryIds.length > 0
                    ? " The chips below are what you have cooked with before."
                    : ""}
                </p>
              </div>
            ) : (
              <ul className="pot__lines">
                {figures.lines.map((line, index) => (
                  <li className="pot__line" key={`${line.food.id}:${index}`}>
                    <button
                      className="pot__line-body"
                      onClick={() => weigh(line.food, index, line.grams)}
                    >
                      <span className="pot__line-name">{line.food.name}</span>
                      <span className="pot__line-meta num">
                        {formatGrams(line.grams)} {line.food.basis_unit} ·{" "}
                        {Math.round(line.kcal)} kcal
                      </span>
                    </button>
                    <button
                      className="pot__remove"
                      onClick={() => actions.remove(index)}
                      aria-label={`Take ${line.food.name} out of the pot`}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="pot__adders">
              <button className="pot__add" onClick={() => setStep({ kind: "search" })}>
                Add an ingredient
              </button>
              <button className="pot__add" onClick={() => setStep({ kind: "scan" })}>
                Scan a barcode
              </button>
            </div>

            {pantryIds.length > 0 && (
              <div className="pot__pantry">
                {pantryIds.map(([id, grams]) => {
                  const food = pantry.byId[id];
                  if (!food) return null;
                  return (
                    <button
                      key={id}
                      className="pot__chip"
                      onClick={() => weigh(food, null, grams)}
                    >
                      {food.name}
                      <span className="pot__chip-detail num">{formatGrams(grams)} g</span>
                    </button>
                  );
                })}
              </div>
            )}

            {!figures.empty && (
              <dl className="pot__totals">
                <div>
                  <dt>Raw weight</dt>
                  <dd className="num">{round(figures.rawG)} g</dd>
                </div>
                <div>
                  <dt>The whole pot</dt>
                  <dd className="num">{round(figures.totals.kcal)} kcal</dd>
                </div>
                <div>
                  <dt>Macros</dt>
                  <dd className="num">
                    P {Math.round(figures.totals.protein_g)} · C{" "}
                    {Math.round(figures.totals.carbs_g)} · F {Math.round(figures.totals.fat_g)}
                  </dd>
                </div>
              </dl>
            )}
          </>
        )}
      </div>

      {step.kind === "search" ? (
        <IngredientSearch
          query={query}
          onQuery={setQuery}
          results={results.data}
          loading={results.isLoading}
          onPick={(food) => weigh(food, null)}
          onCancel={() => {
            setQuery("");
            setStep({ kind: "list" });
          }}
        />
      ) : step.kind === "weigh" ? (
        <GramsPad
          name={step.food.name}
          value={grams}
          onChange={setGrams}
          unit={step.food.basis_unit}
          per100={perHundred(step.food)}
          chips={suggestedPortions(
            step.food,
            habits.data && habits.data.length ? habits.data : [],
          )}
          action={step.index === null ? "Into the pot" : "Change it"}
          onCommit={commitWeight}
        />
      ) : step.kind === "name" ? (
        <div className="pot__naming">
          <input
            className="sheet__search"
            value={name}
            autoFocus
            aria-label="Name this meal"
            placeholder="Name this meal"
            onChange={(event) => setName(event.target.value)}
          />
          <button className="pot__go" onClick={save} disabled={saveRecipe.isPending}>
            {saveRecipe.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      ) : (
        <>
          {/* Naming is at the end rather than the start, and it is an offer
              rather than a step: a pot cooked exactly as its recipe is never
              asked, because the recipe already exists. */}
          {offer && (
            <div className="pot__offer">
              <p className="pot__offer-text">{offer.suggestion}</p>
              <button
                className="pot__offer-yes"
                onClick={() => {
                  setName(draft.source ? `${draft.source.name} ×${draft.scale}` : name);
                  setStep({ kind: "name" });
                }}
              >
                {offer.action}
              </button>
              <button className="pot__offer-no" onClick={actions.dismissSave}>
                Not now
              </button>
            </div>
          )}

          <div className="pot__dock">
            {/* Inert until something is in the pot. Dividing nothing by four is
                the empty state that produces a batch of zero-calorie portions. */}
            <button className="pot__go" onClick={onDivide} disabled={figures.empty}>
              {figures.empty
                ? "Divide into portions"
                : `Divide ${round(figures.rawG)} g into portions`}
            </button>
          </div>
        </>
      )}
    </>
  );
}

/**
 * Nothing in the pot yet, and nothing chosen to fill it from.
 *
 * A saved meal leads, because most pots are something cooked before and picking
 * one fills the pot at its saved weights — typing ingredients in is the
 * fallback rather than the front door.
 */
function StartFrom({
  recipes,
  query,
  onQuery,
  onPick,
  onScratch,
}: {
  recipes: Recipe[] | undefined;
  query: string;
  onQuery: (next: string) => void;
  onPick: (recipe: Recipe) => void;
  onScratch: () => void;
}) {
  const matching = (recipes ?? []).filter((recipe) =>
    recipe.name.toLowerCase().includes(query.trim().toLowerCase()),
  );

  return (
    <div className="pot__start">
      {matching.length > 0 && (
        <>
          <h3 className="sheet__heading">Start from a saved meal</h3>
          <ul className="pot__recipes">
            {matching.slice(0, 8).map((recipe) => (
              <li key={recipe.id}>
                <button className="pot__recipe" onClick={() => onPick(recipe)}>
                  <span className="pot__line-name">{recipe.name}</span>
                  <span className="pot__line-meta num">
                    {recipe.items.length} ingredients ·{" "}
                    {round(recipe.items.reduce((total, item) => total + item.grams, 0))} g
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <input
            className="sheet__search"
            value={query}
            placeholder="Search your meals"
            aria-label="Search your meals"
            onChange={(event) => onQuery(event.target.value)}
          />
        </>
      )}

      <p className="pot__or">{matching.length > 0 ? "or build it here" : "Build it here"}</p>
      {/* Drawn as the primary action of this pane rather than as one more
          dashed box, because for anyone with no saved meal to start from it is
          the only way forward. Scanning is not offered here: a barcode is one
          ingredient, and one ingredient is not a way to start a pot — it lives
          inside the builder, next to "Add an ingredient", where it is the same
          size as the job it does. */}
      <button className="pot__scratch" onClick={onScratch}>
        <span className="pot__scratch-name">Start from scratch</span>
        <span className="pot__line-meta">Add ingredients as they go in</span>
      </button>

      <p className="muted pot__note">
        A saved meal fills the pot at its saved weights, and every one of them stays editable —
        change a gram figure, drop an ingredient, add what you improvised. The recipe is untouched
        either way.
      </p>
    </div>
  );
}

/** Find something to put in the pan. The system keyboard, because it is words. */
function IngredientSearch({
  query,
  onQuery,
  results,
  loading,
  onPick,
  onCancel,
}: {
  query: string;
  onQuery: (next: string) => void;
  results: FoodItem[] | undefined;
  loading: boolean;
  onPick: (food: FoodItem) => void;
  onCancel: () => void;
}) {
  return (
    <div className="pot__search">
      <ul className="pot__results">
        {loading && <li className="muted">Searching…</li>}
        {!loading && query.trim() !== "" && results?.length === 0 && (
          <li className="muted">Nothing matched.</li>
        )}
        {results?.slice(0, 8).map((food) => (
          <li key={food.id}>
            <button className="pot__line-body" onClick={() => onPick(food)}>
              <span className="pot__line-name">{food.name}</span>
              <span className="pot__line-meta num">
                {Math.round(food.kcal_per_100)} kcal / 100 {food.basis_unit}
              </span>
            </button>
          </li>
        ))}
      </ul>
      <div className="pot__dock">
        <input
          className="sheet__search"
          value={query}
          autoFocus
          placeholder="Search foods"
          aria-label="Search foods"
          onChange={(event) => onQuery(event.target.value)}
        />
        <button className="sheet__scan" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ========================================================================== *
 * 7b — dividing the pot
 * ========================================================================== */

/**
 * Best-before, as four taps rather than a date picker.
 *
 * The three dates are the ones anybody means — tomorrow, the day after, the end
 * of the week — and "Froze it" is the fourth answer, which is "no date": frozen
 * food does not have a best-before in the sense this rail uses one, and an
 * invented one would put an amber bar on a pot that is fine.
 */
function bestBeforeOptions(from: Date): { label: string; date: string }[] {
  return [1, 2, 4].map((days) => {
    const when = new Date(from);
    when.setDate(when.getDate() + days);
    return {
      label: when.toLocaleDateString(undefined, { weekday: "short" }),
      date: when.toISOString().slice(0, 10),
    };
  });
}

export function PotDivider({
  draft,
  figures,
  actions,
  storing,
  onBack,
  onStore,
}: {
  draft: PotDraft;
  figures: PotFigures;
  actions: PotActions;
  storing: boolean;
  onBack: () => void;
  onStore: () => void;
}) {
  /** Whether the pad is up on the pot's own weight. */
  const [weighing, setWeighing] = useState(false);
  const options = useMemo(() => bestBeforeOptions(new Date()), []);
  const portions = Math.max(1, draft.portions);

  /**
   * What the weight is for, said where the decision is made.
   *
   * Both halves are worth stating because both are true and only one of them
   * is obvious: without a weight the calories are still right, and the thing
   * that is missing is a gram figure to log against.
   */
  const weightNote = figures.empty
    ? "Nothing in the pot to weigh."
    : figures.cookedG !== null
      ? `${round(figures.rawG)} g of ingredients came out as ${round(figures.cookedG)} g cooked — ${Math.round(
          figures.lossPct ?? 0,
        )}% off as water. A portion is ${Math.round(
          figures.portionG ?? 0,
        )} g at ${Math.round(figures.kcalPer100Cooked ?? 0)} kcal / 100 g, so it can be logged by weight instead of as a fraction.`
      : `Without a weight a portion is one ${portions}th of the recipe's arithmetic — right on calories, but there is no gram figure to log against and no per-100 g density.`;

  if (weighing) {
    return (
      <GramsPad
        name={`${draft.name}, cooked`}
        value={draft.cooked}
        onChange={actions.setCooked}
        unit="g"
        // Nothing here is being logged, so the pad's usual readout — the
        // calories in this weight of this food — would be a number about
        // nothing. The figures below say what the weight actually buys.
        per100={{ kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0 }}
        figures={[
          { label: "Raw ingredients", value: `${round(figures.rawG)} g` },
          {
            label: "Cooked off",
            value:
              figures.lossG === null
                ? "—"
                : `${round(figures.lossG)} g · ${Math.round(figures.lossPct ?? 0)}%`,
          },
          {
            label: "Per 100 g cooked",
            value:
              figures.kcalPer100Cooked === null
                ? "—"
                : `${Math.round(figures.kcalPer100Cooked)} kcal`,
          },
        ]}
        chips={[
          {
            kind: "round" as const,
            label: `Raw ${round(figures.rawG)} g`,
            grams: Math.round(figures.rawG),
            detail: `the ingredients, unreduced — ${round(figures.rawG)} g`,
          },
        ]}
        // What the ingredients weighed, offered because a pot that lost no
        // water weighs exactly that. It is a reading rather than a helping, so
        // tapping it twice is still one pot.
        chipsAdd={false}
        // Nothing is logged here: the pot goes into store and the eating comes
        // later, so the key that says "Add" everywhere else says what this
        // actually does.
        action="Done"
        onCommit={() => setWeighing(false)}
      />
    );
  }

  return (
    <>
      <div className="sheet__scroll pot">
        <div className="pot__section">
          <h3 className="sheet__heading">What you cooked</h3>
        </div>
        <div className="pot__cooked">
          <div className="pot__title">
            <span className="pot__name">{draft.name}</span>
            <span className="pot__source num">
              {figures.lines.length}{" "}
              {figures.lines.length === 1 ? "ingredient" : "ingredients"} ·{" "}
              {round(figures.rawG)} g raw
            </span>
          </div>
          <button className="pot__change" onClick={onBack}>
            Change
          </button>
        </div>

        {figures.empty && (
          <p className="muted pot__note">
            Not a saved meal yet?{" "}
            <button className="pot__link" onClick={onBack}>
              Build it from ingredients
            </button>{" "}
            — the pot cannot be divided until something knows what is in it.
          </p>
        )}

        <div className="pot__section">
          <h3 className="sheet__heading">How many portions</h3>
        </div>
        <div className="pot__portions">
          <button
            className="pot__step"
            onClick={() => actions.setPortions(portions - 1)}
            aria-label="One portion fewer"
          >
            −
          </button>
          <span className="pot__portion-count">
            <span className="num">{portions}</span>
            <span className="pot__portion-label">portions out of this pot</span>
          </span>
          <button
            className="pot__step"
            onClick={() => actions.setPortions(portions + 1)}
            aria-label="One portion more"
          >
            +
          </button>
        </div>

        <div className="pot__portion">
          <span className="pot__portion-head">One portion</span>
          <span className="pot__portion-unit num">
            {figures.portionG === null
              ? "—"
              : figures.cookedG !== null
                ? `${Math.round(figures.portionG)} g`
                : `1 of ${portions}`}
          </span>
          <span className="pot__portion-kcal num">
            {Math.round(figures.perPortion.kcal)} <small>kcal</small>
          </span>
          <span className="pot__portion-macros num">
            P {Math.round(figures.perPortion.protein_g)} · C{" "}
            {Math.round(figures.perPortion.carbs_g)} · F {Math.round(figures.perPortion.fat_g)}
          </span>
        </div>

        <div className="pot__section">
          <h3 className="sheet__heading">Weigh the pot</h3>
          <button
            className="pot__link"
            onClick={() => {
              if (figures.cookedG !== null) actions.setCooked("");
              else setWeighing(true);
            }}
            disabled={figures.empty}
          >
            {figures.cookedG !== null ? "Skip it" : "Weigh it"}
          </button>
        </div>
        {figures.cookedG !== null && (
          <button className="pot__weight" onClick={() => setWeighing(true)}>
            <span>Cooked weight</span>
            <span className="num">
              {round(figures.cookedG)} <small>g</small>
            </span>
          </button>
        )}
        <p className="muted pot__note">{weightNote}</p>

        <div className="pot__section">
          <h3 className="sheet__heading">Best before</h3>
        </div>
        <div className="pot__dates">
          {options.map((option) => (
            <button
              key={option.date}
              className={`pot__chip${draft.bestBefore === option.date ? " is-on" : ""}`}
              onClick={() =>
                actions.setBestBefore(draft.bestBefore === option.date ? null : option.date)
              }
            >
              {option.label}
            </button>
          ))}
          <button
            className={`pot__chip${draft.bestBefore === null ? " is-on" : ""}`}
            onClick={() => actions.setBestBefore(null)}
          >
            Froze it
          </button>
        </div>
      </div>

      <div className="pot__dock">
        <button className="pot__go" onClick={onStore} disabled={figures.empty || storing}>
          {storing ? "Storing…" : `Store ${portions} portions`}
        </button>
      </div>
    </>
  );
}
